import type { TFunction } from 'i18next'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import type { InvoiceTransaction } from '@/modules/Invoice/types'
import {
    TRANSACTION_PAYMENT_METHODS,
    TRANSACTION_STATUS,
} from '@/modules/Invoice/constants'
import { formatCurrency, formatNumeral } from '@/utils/function'
import { Badge } from '@/components/ui/badge'
import { createCurrencyFormatter } from '@/components/ui/data-table-customized'

export const getTransactionColumns = (
    t: TFunction
): DataTableColumn<InvoiceTransaction>[] => [
    {
        key: 'invoice_transaction_id',
        title: t('transaction-ID'),
        width: 140,
    },
    {
        key: 'employee_name',
        title: t('employee_name'),
        width: 180,
    },
    {
        key: 'transaction_note',
        title: t('transaction_note'),
        width: 300,
        cellClassName: 'truncate',
    },
    {
        key: 'invoice_transaction_before_exchange_rate',
        title: t('invoice_transaction_before_exchange_rate'),
        width: 180,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'currency',
        title: t('currency'),
        width: 100,
    },
    {
        key: 'exchange_rate',
        title: t('exchange_rate'),
        width: 130,
        render: (value) => (
            <span className="w-full text-right block text-muted-foreground">
                {formatNumeral(value, 0) || 0}
            </span>
        ),
    },
    {
        key: 'transaction_amount',
        title: t('transaction_amount'),
        width: 200,
        render: (value) => (
            <span className="w-full text-right block font-medium">
                {formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'transaction_fee',
        title: t('transaction_fee'),
        width: 180,
        render: (value) => (
            <span className="w-full text-right block">
                {formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'transaction_method',
        title: t('transaction_method'),
        width: 200,
        render: (value) => {
            const method = TRANSACTION_PAYMENT_METHODS(t)?.find(
                (m) => m.value === value
            )
            return method?.label || value || '-'
        },
    },
    {
        key: 'transaction_status',
        title: t('transaction_status'),
        width: 180,
        render: (value) => {
            const status = TRANSACTION_STATUS(t)?.find((s) => s.value === value)
            const variantMap: Record<
                string,
                'default' | 'secondary' | 'destructive' | 'outline'
            > = {
                warning: 'secondary',
                success: 'default',
                danger: 'destructive',
                error: 'destructive',
            }
            return (
                <Badge variant={variantMap[status?.color || ''] || 'outline'}>
                    {status?.label || value}
                </Badge>
            )
        },
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 180,
        render: (value) => (
            <span className="text-muted-foreground">
                {value
                    ? new Date(value).toLocaleString('vi-VN', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                      })
                    : '-'}
            </span>
        ),
    },
]
